import { useEffect, useRef, useState } from "react";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BANNERS } from "@/lib/banners";
import { Card, Eyebrow, PageHead, Toggle } from "@/components/admin/ui";
import { Check, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { clearBrandLogoCache } from "@/lib/brandLogo";
import { LogoCropper } from "@/components/admin/LogoCropper";

export default function Branding() {
  const [banner, setBanner] = useState("brand");
  const [defaults, setDefaults] = useState({ default_waiting_room: false, default_ask_whatsapp: true });
  const [logo, setLogo] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    document.title = "Branding · Udyog Classes";
    api
      .get("/branding")
      .then(({ data: b }) => {
        setBanner(b?.join_banner || "brand");
        setLogo(assetUrl(b?.logo_url));
        setDefaults({
          default_waiting_room: !!b?.default_waiting_room,
          default_ask_whatsapp: b?.default_ask_whatsapp !== false,
        });
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    // SVGs are vector — cropping them to a bitmap would only lose quality.
    if (file.type === "image/svg+xml") {
      uploadLogo(file);
      return;
    }
    setPendingFile(file);
  };

  const uploadLogo = async (blob) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", blob, blob.name || "logo.png");
      const { data } = await api.post("/branding/logo", form);
      setLogo(assetUrl(data?.logo_url));
      clearBrandLogoCache();
      setPendingFile(null);
      toast.success("Logo saved — it's now your contact card icon in class");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      await api.delete("/branding/logo");
      setLogo(null);
      clearBrandLogoCache();
      toast.success("Logo removed — the Udyog mark is back");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveBanner = async (id) => {
    const prev = banner;
    setBanner(id);
    try {
      await api.put("/branding", { join_banner: id });
      toast.success("Join screen banner updated");
    } catch (e) {
      setBanner(prev);
      toast.error(formatApiError(e));
    }
  };

  const saveDefault = async (key, value) => {
    const prev = defaults;
    setDefaults({ ...defaults, [key]: value });
    try {
      const { data } = await api.put("/branding", { [key]: value });
      setDefaults({
        default_waiting_room: !!data?.default_waiting_room,
        default_ask_whatsapp: data?.default_ask_whatsapp !== false,
      });
      toast.success("Default updated");
    } catch (e) {
      setDefaults(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-branding">
      <PageHead
        eyebrow="Branding"
        title="How students see you"
        sub="Your logo, the join banner and the defaults every student meets before joining your class."
      />

      <Card className="p-5" testid="logo-card">
        <div className="mb-4">
          <Eyebrow>Your brand logo</Eyebrow>
          <div className="text-[12px] text-white/40 mt-1">
            Shown as a circle on your contact card everywhere in the class — your tile, the People
            list and your chat messages. Pick any image and you can drag and zoom it inside the
            circle before saving. Max 2 MB.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-5">
          <div
            className="h-24 w-24 shrink-0 rounded-full ring-1 ring-emerald-500/40 bg-neutral-950 grid place-items-center overflow-hidden"
            data-testid="logo-preview"
          >
            {logo ? (
              <img src={logo} alt="Brand logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[9px] uppercase tracking-[0.14em] text-white/25 text-center px-2">
                No logo
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2.5">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="logo-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-400 text-black text-[13px] font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {logo ? "Replace logo" : "Upload logo"}
            </button>
            {logo && (
              <button
                onClick={removeLogo}
                disabled={busy}
                data-testid="logo-remove-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full ring-1 ring-white/12 text-[12.5px] text-white/60 hover:text-white hover:ring-white/25 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove
              </button>
            )}
            <span className="text-[10.5px] text-white/30 max-w-[220px]">
              {logo
                ? "Live now — this is exactly how it appears on your contact card in class."
                : "Until you upload one, the Udyog mark is used."}
            </span>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={pickLogo}
          className="hidden"
          data-testid="logo-file-input"
        />
      </Card>

      {pendingFile && (
        <LogoCropper
          file={pendingFile}
          busy={busy}
          onCancel={() => setPendingFile(null)}
          onSave={uploadLogo}
        />
      )}

      <Card className="p-5" testid="banner-picker" id="branding-card">
        <div className="mb-4">
          <Eyebrow>Join screen banner</Eyebrow>
          <div className="text-[12px] text-white/40 mt-1">
            This image sits above the join form on every class link you share.
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          {BANNERS.map((b) => {
            const active = b.id === banner;
            return (
              <button
                key={b.id}
                onClick={() => saveBanner(b.id)}
                data-testid={`banner-option-${b.id}`}
                className={`relative rounded-2xl overflow-hidden aspect-video transition-transform duration-200 hover:-translate-y-0.5 ${
                  active ? "ring-2 ring-emerald-400" : "ring-1 ring-white/10"
                }`}
              >
                <img src={b.url} alt={b.label} className="w-full h-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 px-2 py-1 bg-black/65 text-[9px] text-white/85 truncate">
                  {b.label}
                </span>
                {active && (
                  <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-400 grid place-items-center">
                    <Check className="w-3 h-3 text-black" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 space-y-4 max-w-xl" testid="class-defaults">
        <Eyebrow>Defaults for new classes</Eyebrow>
        <Toggle
          testid="default-default_waiting_room"
          label="Waiting room"
          hint="You approve each student before they enter"
          on={!!defaults.default_waiting_room}
          onClick={() => saveDefault("default_waiting_room", !defaults.default_waiting_room)}
        />
        <Toggle
          testid="default-default_ask_whatsapp"
          label="Ask WhatsApp on join"
          hint="Builds your student directory automatically"
          on={!!defaults.default_ask_whatsapp}
          onClick={() => saveDefault("default_ask_whatsapp", !defaults.default_ask_whatsapp)}
        />
        <p className="text-[10.5px] text-white/30 pt-1">
          Applies to classes you create from now on. Existing classes keep their own settings.
        </p>
      </Card>
    </div>
  );
}
